import React, {useCallback, useMemo, useState} from 'react'

type Context = {
  value: Record<string, boolean>
  setVisible: (name: string) => void
  setInvisible: (name: string) => void
  getIsVisible: (name: string) => boolean
  register: (name: string) => void
}

const BlocksVisibilityStatesContext = React.createContext<Context | null>(null)

export const useBlocksVisibilityStatesContext = () => {
  const context = React.useContext(BlocksVisibilityStatesContext)

  if (!context) {
    throw new Error('useBlocksVisibilityStates must be used within a BlocksVisibilityStatesProvider')
  }

  return context
}

type Props = React.PropsWithChildren<unknown>

const BlocksVisibilityStatesProvider: React.FC<Props> = ({children}) => {
  const [contextValue, setValue] = useState<Record<string, boolean>>({
    additionalRequirements: false,
  })

  const register = useCallback((name: string) => {
    setValue(prevState => ({...prevState, [name]: false}))
  }, [])

  const setVisible = useCallback((name: string) => {
    setValue(prevState => ({...prevState, [name]: true}))
  }, [])

  const setInvisible = useCallback((name: string) => {
    setValue(prevState => ({...prevState, [name]: false}))
  }, [])

  const getIsVisible = useCallback(
    (name: string) => {
      return Boolean(contextValue[name])
    },
    [contextValue],
  )

  const value = useMemo(
    () => ({
      value: contextValue,
      register,
      setVisible,
      setInvisible,
      getIsVisible,
    }),
    [register, contextValue, setVisible, setInvisible, getIsVisible],
  )

  return <BlocksVisibilityStatesContext.Provider value={value}>{children}</BlocksVisibilityStatesContext.Provider>
}

BlocksVisibilityStatesProvider.displayName = 'BlocksVisibilityStatesProvider'

export default BlocksVisibilityStatesProvider
